import { useCallback, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { useMessageManager } from '@framework/message';
import ContactRepository from '@/creditflowresearch/infrastructure/repository/ContactRepository';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import useGetItemsUserPreferences from './useGetItemsUserPreferences';
import { IUserPlanPreferences } from '@creditflowresearch/user-plan/vo/IUserPlanResponse';
import {
  getMapActiveItemIds,
  getPreferenceIds,
  mergePreferenceIds,
  IPreferenceItem,
} from '@/creditflowresearch/application/hooks/userPreferences/helpers';
import { togglePreferenceId } from '@/creditflowresearch/application/helpers/togglePreferenceId';
import {
  isSegmentPreferenceItem,
  toggleSegmentPreference,
  withResolvedSegments,
} from '@/creditflowresearch/application/hooks/userPreferences/segmentPreferences';
import {
  toCompletePreferences,
} from '@/creditflowresearch/application/hooks/userPreferences/toCompletePreferences';

export const useUserPlanPreferences = () => {
  const container = getContainer();
  const { getErrorMessage } = useMessageManager();
  const { preferences, hasSavedPreferences, setUserPlanPreferences } = useUserPlanContext();
  const {
    items,
    currentChildren,
    segmentItems,
    canActive,
    canAccess,
    accessiblePreferences,
  } = useGetItemsUserPreferences();

  const [isFetching, setIsFetching] = useState(false);

  const updatePreferences = useCallback(
    async (newPrefs: Partial<IUserPlanPreferences>, isParentChange = false) => {
      try {
        setIsFetching(true);

        const updated = withResolvedSegments({
          ...(!isParentChange && accessiblePreferences),
          ...newPrefs,
        }, accessiblePreferences);

        const contactRepository = container.get(ContactRepository);
        await contactRepository.updateUserSubscriptionPlanPreferences(toCompletePreferences(updated));

        setUserPlanPreferences(updated);
      } catch (error) {
        AlertManager.error(getErrorMessage(error));
      } finally {
        setIsFetching(false);
      }
    },
    [container, accessiblePreferences, setUserPlanPreferences, getErrorMessage],
  );

  const addNewItemPreference = useCallback((newItem: IPreferenceItem) => {
    if (isFetching) {
      return;
    }

    if (isSegmentPreferenceItem(newItem)) {
      updatePreferences(toggleSegmentPreference(accessiblePreferences, newItem));

      return;
    }

    const hasChildren = !newItem.children;
    const mapChildren = hasChildren
      ? {}
      : getMapActiveItemIds(newItem.children || [], canAccess, canActive);
    const existingValues = getPreferenceIds(accessiblePreferences, newItem.fieldName);
    const newValues = hasChildren
      ? togglePreferenceId(newItem.id, existingValues)
      : mergePreferenceIds(newItem, items, existingValues);
    const updates = { [newItem.fieldName]: newValues, ...mapChildren };

    updatePreferences(updates, !hasChildren);
  }, [updatePreferences, isFetching, accessiblePreferences, items, canAccess, canActive]);

  return {
    preferences,
    updatePreferences,
    addNewItemPreference,
    isFetching,
    hasSavedPreferences,
    items,
    currentChildren,
    segmentItems,
    canActive,
    canAccess,
  };
};
